import React, { useState } from 'react';
import { Mail, Phone, MapPin, Home, Edit2, CheckCircle, AlertCircle, Globe, ChevronDown } from 'lucide-react';
import { ToastContainer } from "react-toastify";
import { countryCodes } from '../../Login/components/CountryCode';

const ContactForm = ({ loading, errors, preview, handleChange, formData, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localErrors, setLocalErrors] = useState({});
    const [expandedSections, setExpandedSections] = useState({});

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Enhanced input styling with responsive classes
    const baseInput = "w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base outline-none transition-all duration-200 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300";
    const textareaClass = "w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base outline-none transition-all duration-200 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 resize-none";

    const validateForm = () => {
        const newErrors = {};
        if (!formData.indiaAddress?.trim()) newErrors.indiaAddress = "India address is required";
        if (!formData.canadaAddress?.trim()) newErrors.canadaAddress = "Canada address is required";
        if (!formData.email?.trim()) newErrors.email = "Email is required";
        if (!formData.email?.includes('@')) newErrors.email = "Valid email is required";
        if (!formData.email?.includes('.')) newErrors.email = "Valid email is required";
        if (!formData.mobile?.trim()) newErrors.mobile = "Mobile number is required";
        if (formData.mobile && !/^[0-9+\-\s]{8,15}$/.test(formData.mobile)) newErrors.mobile = "Valid mobile number required";

        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    // If in preview mode
    if (preview && !isEditing) {
        return (
            <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                                <Home size={24} className="text-blue-300" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Contact Information</h1>
                                <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Your verified contact details</p>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">
                    {preview?.status === "approve" && (
                        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                            <CheckCircle size={20} className="text-green-600 shrink-0" />
                            <p className="text-sm font-medium text-green-800">Contact information verified and approved</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-4 sm:gap-5">
                        <PreviewCard
                            icon={<MapPin size={18} />}
                            label="India Address"
                            value={preview.indiaAddress}
                            fullWidth
                        />
                        <PreviewCard
                            icon={<MapPin size={18} />}
                            label="Canada Address"
                            value={preview.canadaAddress}
                            fullWidth
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                            <PreviewCard
                                icon={<Mail size={18} />}
                                label="Email Address"
                                value={preview.email}
                                subText="Primary contact email"
                            />
                            <PreviewCard
                                icon={<Phone size={18} />}
                                label="Mobile Number"
                                value={`${preview.countryCodes || '+1'} ${preview.mobile}`}
                                subText="WhatsApp enabled"
                            />
                        </div>
                    </div>

                    {(preview?.status === "reject" || !preview?.status) && (
                        <div className="flex justify-center pt-6 mt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold text-sm transition-all shadow-md"
                            >
                                <Edit2 size={16} /> Edit Contact Information
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Saving changes...</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                        <Home size={24} className="text-blue-300" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Contact Information</h1>
                        <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Please provide your contact details</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">

                {/* Error Display */}
                {(Object.keys(localErrors).length > 0 || errors?.length > 0) && (
                    <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                        <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                            <AlertCircle size={18} /> Please correct the following:
                        </div>
                        <ul className="space-y-1">
                            {Object.values(localErrors).map((err, i) => (
                                <li key={i} className="text-rose-600 text-[11px] flex items-center gap-1">
                                    <AlertCircle size={10} /> {err}
                                </li>
                            ))}
                            {errors?.map((err, i) => (
                                <li key={`err-${i}`} className="text-rose-600 text-[11px] flex items-center gap-1">
                                    <AlertCircle size={10} /> {err}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                    {/* Address Section - Collapsible on mobile */}
                    <div className={`bg-white border border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden transition-all
                        ${expandedSections.address ? 'shadow-md' : 'shadow-sm'}`}>
                        <div
                            className="flex justify-between items-center p-4 sm:p-5 cursor-pointer md:cursor-default bg-gradient-to-r from-blue-50 to-white border-b border-gray-100"
                            onClick={() => toggleSection('address')}
                        >
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                                    <Home size={18} className="text-blue-600" />
                                </div>
                                <h3 className="font-bold text-gray-800 text-sm sm:text-base">Residential Details</h3>
                            </div>
                            <button className="md:hidden text-gray-400">
                                <ChevronDown size={18} className={`transition-transform ${expandedSections.address ? 'rotate-180' : ''}`} />
                            </button>
                        </div>
                        <div className={`
                            transition-all duration-300 overflow-hidden
                            ${expandedSections.address !== false ? 'max-h-[1000px] opacity-100' : 'max-h-0 md:max-h-[1000px] md:opacity-100'}
                        `}>
                            <div className="p-4 sm:p-5 space-y-4 sm:space-y-5">
                                <InputWrapper label="India Address" error={localErrors.indiaAddress || errors?.indiaAddress} required fullWidth>
                                    <textarea
                                        name="indiaAddress"
                                        rows={3}
                                        value={formData.indiaAddress || ""}
                                        onChange={handleChange}
                                        placeholder="House No, Street Name, Landmark, City, District, State - PIN Code"
                                        className={`${textareaClass} placeholder:text-gray-400`}
                                    />
                                    <p className="text-[10px] text-gray-400 mt-1">Complete address as per official records</p>
                                </InputWrapper>

                                <InputWrapper label="Canada Address" error={localErrors.canadaAddress || errors?.canadaAddress} required fullWidth>
                                    <textarea
                                        name="canadaAddress"
                                        rows={3}
                                        value={formData.canadaAddress || ""}
                                        onChange={handleChange}
                                        placeholder="Apt/Suite, Street Address, City, Province, Postal Code"
                                        className={`${textareaClass} placeholder:text-gray-400`}
                                    />
                                    <p className="text-[10px] text-gray-400 mt-1">Current residential address in Canada</p>
                                </InputWrapper>
                            </div>
                        </div>
                    </div>

                    {/* Communication Section - Collapsible on mobile */}
                    <div className={`bg-white border border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden transition-all
                        ${expandedSections.communication ? 'shadow-md' : 'shadow-sm'}`}>
                        <div
                            className="flex justify-between items-center p-4 sm:p-5 cursor-pointer md:cursor-default bg-gradient-to-r from-blue-50 to-white border-b border-gray-100"
                            onClick={() => toggleSection('communication')}
                        >
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                                    <Phone size={18} className="text-blue-600" />
                                </div>
                                <h3 className="font-bold text-gray-800 text-sm sm:text-base">Communication Details</h3>
                            </div>
                            <button className="md:hidden text-gray-400">
                                <ChevronDown size={18} className={`transition-transform ${expandedSections.communication ? 'rotate-180' : ''}`} />
                            </button>
                        </div>
                        <div className={`
                            transition-all duration-300 overflow-hidden
                            ${expandedSections.communication !== false ? 'max-h-[1000px] opacity-100' : 'max-h-0 md:max-h-[1000px] md:opacity-100'}
                        `}>
                            <div className="p-4 sm:p-5 space-y-4 sm:space-y-5">
                                <InputWrapper label="Email Address" error={localErrors.email || errors?.email} required>
                                    <div className="relative">
                                        <Mail size={16} className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            name="email"
                                            type="email"
                                            value={formData.email || ""}
                                            onChange={handleChange}
                                            placeholder="your.email@example.com"
                                            className={`${baseInput} pl-9 sm:pl-11`}
                                            autoComplete="email"
                                        />
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-1">We'll send notifications to this email</p>
                                </InputWrapper>

                                <InputWrapper label="Mobile Number" error={localErrors.mobile || errors?.mobile} required>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <div className="relative sm:w-32">
                                            <select
                                                name="countryCodes"
                                                value={formData.countryCodes || "+1"}
                                                onChange={handleChange}
                                                className={`${baseInput} appearance-none cursor-pointer pr-8`}
                                            >
                                                {countryCodes.map((c) => (
                                                    <option key={c.code} value={c.code}>
                                                        {c.code} {c.iso}
                                                    </option>
                                                ))}
                                            </select>
                                            <Globe size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                        <input
                                            name="mobile"
                                            type="tel"
                                            value={formData.mobile || ""}
                                            onChange={handleChange}
                                            placeholder="98765 43210"
                                            className={`${baseInput} flex-1`}
                                            autoComplete="tel"
                                        />
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-1">Include area code if outside Canada/US</p>
                                </InputWrapper>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Preview Card Component
const PreviewCard = ({ icon, label, value, subText, fullWidth }) => (
    <div className={`group p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-300 ${fullWidth ? 'sm:col-span-2' : ''}`}>
        <div className="flex items-start gap-3">
            <div className="p-2 bg-gray-50 rounded-lg text-blue-600 group-hover:bg-blue-50 transition-colors shrink-0">
                {icon}
            </div>
            <div className="flex-1 min-w-0">
                <span className="text-[10px] sm:text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                    {label}
                </span>
                <p className="text-sm sm:text-base text-gray-800 font-semibold leading-relaxed break-words">
                    {value || <span className="text-gray-300 font-normal italic">Not specified</span>}
                </p>
                {subText && value && (
                    <p className="text-[10px] text-gray-400 mt-1">{subText}</p>
                )}
            </div>
        </div>
    </div>
);

// Input Wrapper Component
const InputWrapper = ({ label, error, children, fullWidth = false, required = false }) => (
    <div className={`flex flex-col gap-1.5 ${fullWidth ? 'md:col-span-2' : ''}`}>
        <label className="text-xs sm:text-sm font-semibold text-gray-700 ml-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {children}
        {error && (
            <span className="text-red-500 text-[10px] sm:text-xs flex items-center gap-1 mt-1">
                <AlertCircle size={12} /> {error}
            </span>
        )}
    </div>
);


export default React.memo(ContactForm);